// Photos uploaded to the backend (vendors' service item photos): uploading and showing them.

// Uploaded files live on the API server. Through the dev proxy that is this page's own origin;
// with REACT_APP_GRAPHQL_URL pointing at the API directly, it is that server's origin.
const API_ORIGIN = process.env.REACT_APP_GRAPHQL_URL ? new URL(process.env.REACT_APP_GRAPHQL_URL).origin : '';

export const mediaUrl = (url) => (url && (url.startsWith('/uploads/') || url.startsWith('/files/')) ? `${API_ORIGIN}${url}` : url);

const MAX_SIDE = 1600;

// Shrinks a photo to at most 1600px on its longest side, as JPEG, before it is uploaded
export function prepareImage(file) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) { reject(new Error(`${file.name} is not a photo`)); return; }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`${file.name} could not be read`));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error(`${file.name} is not a photo this browser can open`));
      img.onload = () => {
        const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff'; // transparent PNGs get a white background as JPEG
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error(`${file.name} could not be read`));
  reader.onload = () => resolve(reader.result);
  reader.readAsDataURL(file);
});

// A PDF, by type or (when the browser gives no type) by name
export const isPdfFile = (file) => file.type === 'application/pdf' || (!file.type && /.pdf$/i.test(file.name || ''));

// POSTs an upload to the API and returns its JSON. Errors say what went wrong: the server's own
// message, a file too large for the server, or the server not being reachable.
async function postUpload(path, payload, what) {
  const token = localStorage.getItem('token');
  let res;
  try {
    res = await fetch(`${API_ORIGIN}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }), ...(payload.headers || {}) },
      body: JSON.stringify(payload.body),
    });
  } catch (e) {
    throw new Error(`${what} could not be uploaded: the server could not be reached. Check your connection and try again.`);
  }
  const body = await res.json().catch(() => null);
  if (res.ok && body) return body;
  if (body?.error) throw new Error(body.error);
  if (res.status === 413) throw new Error(`${what} is too large for the server`);
  if (res.status === 401) throw new Error('Your session has ended: sign in again to upload');
  throw new Error(`${what} could not be uploaded (server error ${res.status})`);
}

// Uploads a voucher (PDF or image) or photo for a booking stage; resolves to { id, name, kind, mimeType, url }
export async function uploadBookingFile(selectionId, kind, file) {
  const isPdf = isPdfFile(file);
  if (isPdf && kind === 'photo') throw new Error(`${file.name}: photos must be images`);
  if (isPdf && file.size > 5 * 1024 * 1024) throw new Error(`${file.name} is larger than 5 MB`);
  const dataUrl = isPdf ? await readAsDataUrl(file) : await prepareImage(file);
  return postUpload('/uploads/booking-files', { body: { selectionId, kind, fileName: file.name, dataUrl } }, file.name);
}

// Uploads one photo file; resolves to its URL (e.g. /uploads/offerings/<id>.jpg)
export async function uploadOfferingPhoto(file) {
  const dataUrl = await prepareImage(file);
  return (await postUpload('/uploads/offering-photos', { body: { dataUrl } }, file.name)).url;
}

// Uploads an identity / address proof or business document (PDF or photo) to the private store;
// vendorUserId: an admin uploading for that vendor. Resolves to { id }.
export async function uploadIdentityDocument(file, { docType, purpose, vendorUserId } = {}) {
  const isPdf = isPdfFile(file);
  if (isPdf && file.size > 5 * 1024 * 1024) throw new Error(`${file.name} is larger than 5 MB`);
  if (!isPdf && !(file.type || '').startsWith('image/')) throw new Error(`${file.name}: upload a PDF or a photo (JPEG, PNG or WebP)`);
  const dataUrl = isPdf ? await readAsDataUrl(file) : await prepareImage(file);
  return postUpload('/uploads/identity-documents', {
    headers: vendorUserId ? { 'X-Vendor-User-Id': vendorUserId } : {},
    body: { docType, purpose, fileName: file.name, dataUrl },
  }, file.name);
}
